import {
  MultiAccountCodeAmount,
  StoreType,
  SubType,
  UpdateTxData
} from "models/business.model";
import { LoginInfo, SignUpData, UpdateUserData } from "models/user.model";
import {
  OtherTaxType,
  PaymentNoticeMatchRequest
} from "models/paymentNotice.model";
import { JournalNewzenFilter } from "models/journal.model";
import { http, rawHttp } from "utils/http";
import { AxiosProgressEvent } from "axios";

export interface BaseParamData {
  startDate?: string;
  endDate?: string;
  invoiceType?: SubType;
}

export interface UploadNewzenData {
  bizCode: string;
  ids: string[]; // 선택된 데이터 ID 목록
  isAll?: boolean; // 전체 데이터 업로드 여부 (true: 이미 전송된 데이터 삭제 후 재전송, false: 이미 전송된 데이터 제외한 나머지만 전송)
  deleteOnly?: boolean; // 데이터 삭제만 원하는 경우 true
  invoiceType?: SubType;
}

export function sendLoginRequest(data: LoginInfo): Promise<any> {
  return rawHttp.post("/login", data);
}

export function sendLogoutRequest(): Promise<any> {
  return http.post("/logout");
}

// 로그인 유저 정보
export function getUserInfo(): Promise<any> {
  return http.get("/user");
}

// 유저 정보 수정
export function updateUserInfo(
  userId: string,
  data: UpdateUserData
): Promise<any> {
  return http.put(`/user/${userId}`, data);
}

// 사업자 목록
export function getBusinessList(): Promise<any> {
  return http.get("/business");
}

const smartCtaUrl = "/smartcta";

const storeTypeUrlBuilder = (type: StoreType): string => {
  switch (type) {
    case "card":
      return "/card-buy";
    case "bank":
      return "/bank-statement";
    case "tax":
      return "/invoice";
    case "journal":
      // 일반전표는 사업자 목록(GET /smartcta/journal)만 이 빌더를 쓴다. 상세 이하는 journalUrl 참고
      return "/journal";
    default:
      throw new Error(
        "Invalid store type provided. Use 'card', 'bank', or 'tax'."
      );
  }
};

// 사업자 회계처리 현황
export function getStores(
  type: StoreType,
  paramData?: BaseParamData
): Promise<any> {
  let reqUrl = smartCtaUrl;
  reqUrl += storeTypeUrlBuilder(type);

  return http.get(reqUrl, { params: paramData });
}

// 회계처리 상세 내역
export function getStore(
  type: StoreType,
  businessId: string,
  paramData: BaseParamData
): Promise<any> {
  let reqUrl = smartCtaUrl;
  reqUrl += storeTypeUrlBuilder(type);

  if (businessId) reqUrl += `/${businessId}`;
  else throw new Error("Business ID is required.");

  return http.get(reqUrl, { params: paramData });
}

// 담당자 배정 후보 목록 (로그인 계정이 속한 세무대리 기관의 소속 직원 이름 목록)
export function getInchargeCandidates(): Promise<any> {
  return http.get("/smartcta/incharge");
}

// 사업자별 담당자 일괄 배정
export function updateIncharge(
  data: { businessId: string; incharge: string | null }[]
): Promise<any> {
  return http.put("/smartcta/incharge", data);
}

// 거래처(사업자)별 자유 메모 전체 조회 (payment-notice-api.md §4.4).
// 응답 data = Map<businessId, memo>. 등록된 메모가 없으면 {}.
export function getSmartCtaMemos(): Promise<any> {
  return http.get("/smartcta/memo");
}

// 거래처 메모 등록/수정/삭제 (payment-notice-api.md §4.3).
// memo가 비어있으면(null/"") 해당 사업자의 메모를 삭제한다. 여러 건을 배열로 보낼 수 있다.
export function updateSmartCtaMemo(
  businessId: string,
  memo: string
): Promise<any> {
  return http.put("/smartcta/memo", [{ businessId, memo }]);
}

// 카카오 알림톡 발신 우선 번호 등록/변경 (숫자 11자리 권장).
// Business.payrollManagerMobileNo(급여담당자 연락처)에 저장된다. URL·바디 키는 그대로.
export function updateKakaoMobileNo(
  businessId: string,
  kakaoMobileNo: string
): Promise<any> {
  return http.put(`/smartcta/${businessId}/kakao-mobile-no`, {
    kakaoMobileNo
  });
}

// 카카오 알림톡 발신 우선 번호 삭제 → Business.payrollManagerMobileNo $unset.
// 삭제 후 발송은 userMobileNo(조직 대표 번호)로 폴백되고, 급여담당자 연락처도 함께 지워진다.
export function deleteKakaoMobileNo(businessId: string): Promise<any> {
  return http.delete(`/smartcta/${businessId}/kakao-mobile-no`);
}

export interface AiSuggestionResult {
  id: string;
  accountCode: string;
  isVatDeduction: boolean;
  reasoning?: string;
}

// 카드 매입 AI 회계처리 추천
export function getCardBuyAiSuggestion(
  businessId: string,
  ids: string[]
): Promise<any> {
  if (!businessId) throw new Error("Business ID is required.");
  return http.post(`${smartCtaUrl}/card-buy/${businessId}/ai-suggestion`, ids);
}

export interface BankAiSuggestionResult {
  id: string;
  accountCode: string;
  reasoning?: string;
  // 값이 있으면 다중계정 분할 추천. String accountCode + BigDecimal amount 객체 리스트.
  multiAccountCodes?: MultiAccountCodeAmount[] | null;
}

// 통장 AI 회계처리 추천
export function getBankStatementAiSuggestion(
  businessId: string,
  ids: string[]
): Promise<any> {
  if (!businessId) throw new Error("Business ID is required.");
  return http.post(
    `${smartCtaUrl}/bank-statement/${businessId}/ai-suggestion`,
    ids
  );
}

export type TaxInvoiceAiSuggestionType = "TAX_INVOICE" | "TAXFREE_INVOICE";

export interface TaxInvoiceBuyAiSuggestionResult {
  id: string;
  invoiceType: TaxInvoiceAiSuggestionType;
  accountCode: string;
  isVatDeduction: boolean | null;
  reasoning?: string;
}

export interface TaxInvoiceSalesAiSuggestionResult {
  id: string;
  invoiceType: TaxInvoiceAiSuggestionType;
  accountCode: string;
  reasoning?: string;
}

export interface TaxInvoiceBuyAiSuggestionRequest {
  taxInvoiceBuyIds: string[];
  taxfreeInvoiceBuyIds: string[];
}

export interface TaxInvoiceSalesAiSuggestionRequest {
  taxInvoiceSalesIds: string[];
  taxfreeInvoiceSalesIds: string[];
}

// 매입세금계산서 AI 회계처리 추천
export function getTaxInvoiceBuyAiSuggestion(
  businessId: string,
  data: TaxInvoiceBuyAiSuggestionRequest
): Promise<any> {
  if (!businessId) throw new Error("Business ID is required.");
  return http.post(
    `${smartCtaUrl}/invoice/buy/${businessId}/ai-suggestion`,
    data
  );
}

// 매출세금계산서 AI 회계처리 추천
export function getTaxInvoiceSalesAiSuggestion(
  businessId: string,
  data: TaxInvoiceSalesAiSuggestionRequest
): Promise<any> {
  if (!businessId) throw new Error("Business ID is required.");
  return http.post(
    `${smartCtaUrl}/invoice/sales/${businessId}/ai-suggestion`,
    data
  );
}

export type PromptType =
  | "card-buy"
  | "bank-statement"
  | "tax-invoice-buy"
  | "tax-invoice-sales";

export interface CustomPromptData {
  content: string | null;
  updatedAt: string | null;
}

const promptTypeUrlBuilder = (
  storeType: StoreType,
  subType?: SubType
): PromptType => {
  switch (storeType) {
    case "card":
      return "card-buy";
    case "bank":
      return "bank-statement";
    case "tax":
      return subType === "SALES" ? "tax-invoice-sales" : "tax-invoice-buy";
    default:
      throw new Error("Invalid store type for custom prompt.");
  }
};

const promptUrl = "/smartcta/prompt";

// AI 회계처리 커스텀 프롬프트 조회
export function getCustomPrompt(
  storeType: StoreType,
  businessId: string,
  subType?: SubType
): Promise<any> {
  if (!businessId) throw new Error("Business ID is required.");
  const type = promptTypeUrlBuilder(storeType, subType);
  return http.get(`${promptUrl}/${type}/${businessId}`);
}

// AI 회계처리 커스텀 프롬프트 등록/수정
export function updateCustomPrompt(
  storeType: StoreType,
  businessId: string,
  content: string,
  subType?: SubType
): Promise<any> {
  if (!businessId) throw new Error("Business ID is required.");
  const type = promptTypeUrlBuilder(storeType, subType);
  return http.put(`${promptUrl}/${type}/${businessId}`, { content });
}

// 회계처리 내용 변경
export function updateTransaction(
  type: StoreType,
  businessId: string,
  data: UpdateTxData[],
  paramData?: { registerAccountCode?: boolean }
) {
  let reqUrl = smartCtaUrl;
  reqUrl += storeTypeUrlBuilder(type);

  if (!businessId) throw new Error("Business ID is required.");
  reqUrl += `/${businessId}`;

  return http.put(reqUrl, data, { params: paramData });
}

// 회계 검수 완료/미완료 처리
export function updateConfirmation(
  type: StoreType,
  businessId: string,
  confirmed: boolean,
  paramData: BaseParamData
) {
  let reqUrl = smartCtaUrl;
  reqUrl += storeTypeUrlBuilder(type);

  if (!businessId) throw new Error("Business ID is required.");
  reqUrl += `/${businessId}/confirmation`;

  return http.put(reqUrl, {
    isConfirmed: confirmed,
    ...paramData
  });
}

// 세무사랑 양식 엑셀 다운로드 (통장은 세무사랑 양식이 없음)
export function downloadNewzenExcel(
  storeType: StoreType,
  businessId: string,
  paramData: {
    startDate: string;
    endDate: string;
    invoiceType?: string;
  }
) {
  let reqUrl = smartCtaUrl;
  reqUrl += storeTypeUrlBuilder(storeType);

  if (!businessId) throw new Error("Business ID is required.");
  reqUrl += `/excel/${businessId}`;

  return rawHttp.get(reqUrl, {
    params: paramData,
    responseType: "blob"
  });
}

// 임시 비밀번호 발급 (이메일)
export function sendTempPassword(mailData: { email: string }) {
  return http.post(`/find-passwd`, mailData);
}

// 유저 인증 코드 발송
export function sendAuthCodeOnDevice(
  device: "email" | "mobile",
  serial: string
) {
  const dvcSerial = device === "email" ? encodeURIComponent(serial) : serial;
  return http.post(`/code/${device}?deviceSerial=${dvcSerial}`);
}

// 유저 인증 코드 검증
export function checkDeviceVeriCode(
  device: "email" | "mobile",
  verId: string,
  code: string
) {
  return http.get(`/code/${device}/${verId}?code=${code}`);
}

// 회원 가입 파일 첨부
export function signUpAttachFile(formData: FormData) {
  return http.post("/attached-file", formData);
}

// 유저 등록 (가입)
export function registerUser(data: SignUpData) {
  return http.post("/register", data);
}

//// 스마트세무사

/// 사업용 신용카드
// 세무사랑 회사 조회 - 카드/은행 공통
export function getNewzenBusinessList(bizId: string): Promise<any> {
  return http.get(`/smartcta/newzen/${bizId}`);
}

// 세무사랑 업로드
export function uploadNewzenStore(
  bizId: string,
  data: UploadNewzenData,
  type?: StoreType
): Promise<any> {
  if (!bizId) throw new Error("Business ID is required.");
  if (!type) throw new Error("Store type is required.");

  if (type === "extra") {
    if (!data.invoiceType) throw new Error("Invoice type is required.");
    return http.post(extraInvoiceUrl(data.invoiceType, bizId, "newzen"), data);
  }

  let reqUrl = smartCtaUrl;
  reqUrl += storeTypeUrlBuilder(type);
  reqUrl += "/newzen";
  reqUrl += `/${bizId}`;

  return http.post(reqUrl, data);
}

// 세무사랑 데이터 동기화 (다운로드)
export function syncNewzenData(
  bizId: string,
  data: UploadNewzenData,
  storeType?: StoreType
): Promise<any> {
  if (!bizId) throw new Error("Business ID is required.");
  if (!storeType) throw new Error("Store type is required.");

  if (storeType === "extra") {
    if (!data.invoiceType) throw new Error("Invoice type is required.");
    return http.put(extraInvoiceUrl(data.invoiceType, bizId, "newzen"), data);
  }

  let reqUrl = smartCtaUrl;
  reqUrl += storeTypeUrlBuilder(storeType);
  reqUrl += "/newzen";
  reqUrl += `/${bizId}`;

  return http.put(reqUrl, data);
}

//// 기타매입/기타매출 (extra-invoice, 수기 입력분)

/**
 * `/smartcta/extra-invoice/{buy|sales}[/newzen]/{businessId}` 형태의 URL 생성.
 * segment: "newzen"이면 businessId 앞에 "/newzen"을 끼워넣음.
 */
const extraInvoiceUrl = (
  subType: SubType,
  businessId?: string,
  segment?: "newzen" | "confirmation"
): string => {
  let reqUrl = `${smartCtaUrl}/extra-invoice/${subType.toLowerCase()}`;
  if (segment === "newzen") reqUrl += "/newzen";
  if (businessId) reqUrl += `/${businessId}`;
  if (segment === "confirmation") reqUrl += "/confirmation";
  return reqUrl;
};

// 기타매입/매출 회계처리 현황
export function getExtraInvoiceStores(
  subType: SubType,
  paramData?: { startDate?: string; endDate?: string; organizationId?: string }
): Promise<any> {
  return http.get(extraInvoiceUrl(subType), { params: paramData });
}

// 기타매입/매출 회계처리 상세 내역
export function getExtraInvoiceStore(
  subType: SubType,
  businessId: string,
  paramData: { startDate: string; endDate: string }
): Promise<any> {
  if (!businessId) throw new Error("Business ID is required.");
  return http.get(extraInvoiceUrl(subType, businessId), {
    params: paramData
  });
}

// 기타매입/매출 회계처리 내용 변경
export function updateExtraInvoiceTransaction(
  subType: SubType,
  businessId: string,
  data: UpdateTxData[]
): Promise<any> {
  if (!businessId) throw new Error("Business ID is required.");
  return http.put(extraInvoiceUrl(subType, businessId), data);
}

// 기타매입/매출 회계 마감/해제
export function updateExtraInvoiceConfirmation(
  subType: SubType,
  businessId: string,
  confirmed: boolean,
  paramData: { startDate: string; endDate: string; incharge?: string }
): Promise<any> {
  if (!businessId) throw new Error("Business ID is required.");
  return http.put(extraInvoiceUrl(subType, businessId, "confirmation"), {
    isConfirmed: confirmed,
    ...paramData
  });
}

// 기타매입/매출 삭제
export function deleteExtraInvoices(
  subType: SubType,
  businessId: string,
  invoiceIds: string[]
): Promise<any> {
  if (!businessId) throw new Error("Business ID is required.");
  if (!invoiceIds.length) throw new Error("Invoice IDs are required.");
  return http.delete(extraInvoiceUrl(subType, businessId), {
    params: { invoiceIds },
    // invoiceIds=a&invoiceIds=b 형태로 직렬화 (서버는 반복 파라미터를 List로 바인딩)
    paramsSerializer: { indexes: null }
  });
}

//// 일반전표 (journal-api.md) — 등록은 PRO MCP가 하고 화면은 조회·삭제·보류 해제·전송·마감만

const journalUrl = `${smartCtaUrl}/journal`;

// 3.2 일반전표 목록·요약
export function getJournals(
  businessId: string,
  paramData: {
    startDate: string;
    endDate: string;
    newzen?: JournalNewzenFilter;
    accountCode?: string;
    storeCode?: string;
  }
): Promise<any> {
  if (!businessId) throw new Error("Business ID is required.");
  return http.get(`${journalUrl}/${businessId}`, { params: paramData });
}

// 3.3 삭제 — 미전송·보류만. 마감·전송 전표가 섞이면 JE001 (detail.ids)
export function deleteJournals(businessId: string, ids: string[]): Promise<any> {
  if (!businessId) throw new Error("Business ID is required.");
  if (!ids.length) throw new Error("Journal IDs are required.");
  return http.delete(`${journalUrl}/${businessId}`, {
    params: { ids },
    // ids=a&ids=b 형태로 직렬화
    paramsSerializer: { indexes: null }
  });
}

// 3.4 보류 해제 — 미전송으로 바꿀 뿐 전송은 하지 않는다
export function releaseJournals(businessId: string, ids: string[]): Promise<any> {
  if (!businessId) throw new Error("Business ID is required.");
  return http.post(`${journalUrl}/release/${businessId}`, { ids });
}

// 3.5 세무사랑 전송 — 일부가 실패해도 S001이므로 data.results로 건별 성공 여부를 판단한다.
// (uploadNewzenStore는 응답을 버리는 구조라 따로 둔다)
export function sendJournalsToNewzen(
  businessId: string,
  data: { bizCode: string; ids: string[] }
): Promise<any> {
  if (!businessId) throw new Error("Business ID is required.");
  return http.post(`${journalUrl}/newzen/${businessId}`, data);
}

// 3.6 마감 설정·해제
export function updateJournalConfirmation(
  businessId: string,
  confirmed: boolean,
  paramData: { startDate: string; endDate: string }
): Promise<any> {
  if (!businessId) throw new Error("Business ID is required.");
  return http.put(`${journalUrl}/${businessId}/confirmation`, {
    ...paramData,
    isConfirmed: confirmed
  });
}

// 4. 인수일 조회 — 기록이 없으면 data 없음
export function getBusinessTakeover(businessId: string): Promise<any> {
  if (!businessId) throw new Error("Business ID is required.");
  return http.get(`${smartCtaUrl}/takeover/${businessId}`);
}

interface MatchHistorySetting {
  modifyPastData: boolean; // 과거 데이터 일괄 수정 여부
  registerKeyword: boolean; // 키워드 등록하여 추후 데이터 일괄 적용 여부
}

export interface MatchStoreStatementRequest extends MatchHistorySetting {
  storeId: string | null; // 거래처 ID (null인 경우 거래처 해제)
}

// 은행입출금내역 매칭 거래처 변경
/**
 * 은행입출금내역 매칭 거래처 변경
 *
 * @param params bankStatementId 은행입출금내역 ID
 * @param body storeId 거래처 ID
 * @param body modifyPastData 과거 데이터 수정 여부
 * @param body registerKeyword 키워드 등록 여부 - 추후 키워드 매칭 데이터 수정 여부
 */
export function putBankStatementMatchingStore(
  bankStatementId: string,
  reqData: MatchStoreStatementRequest
) {
  return http.put(`/resource/bank-statement/${bankStatementId}/store`, reqData);
}

export function putRecordsMatchingStore(
  storeType: StoreType,
  businessId: string,
  storeId: string,
  recordIds: string[]
) {
  let reqUrl = "/store";
  if (!businessId) throw new Error("Business ID is required.");
  reqUrl += "/" + businessId;
  if (!storeId) throw new Error("Store ID is required.");
  reqUrl += "/" + storeId;
  reqUrl += storeTypeUrlBuilder(storeType);

  let reqBodyKey = "";
  switch (storeType) {
    case "bank":
      reqBodyKey = "bankStatementIds";
      break;
    case "card":
      reqBodyKey = "cardBuyIds";
      break;
    default:
      throw new Error("Invalid store type");
  }

  return http.put(reqUrl, {
    [reqBodyKey]: recordIds
  });
}

//// 급여관리 (payment-notice)

const paymentNoticeUrl = "/smartcta/payment-notice";

// 납부서 파일 업로드 (호출당 파일 1개, 업로드와 동시에 OCR 및 사업자 자동 매칭 수행)
export function uploadPaymentNotice(
  organizationId: string,
  file: File,
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
): Promise<any> {
  if (!organizationId) throw new Error("Organization ID is required.");

  const formData = new FormData();
  formData.append("file", file);

  return http.post(`${paymentNoticeUrl}/${organizationId}`, formData, {
    onUploadProgress
  });
}

// 사업자 매칭 실패(businessId == null) 납부서 목록 조회
export function getUnmatchedPaymentNotices(
  organizationId: string,
  params?: { page?: number; size?: number; sort?: string }
): Promise<any> {
  if (!organizationId) throw new Error("Organization ID is required.");
  return http.get(`${paymentNoticeUrl}/${organizationId}/unmatched`, {
    params
  });
}

// 납부서 사업자 수동 매칭 확정 (한 번에 여러 건, 건별로 독립적으로 처리됨 - 응답의 성공 여부는
// 항목별 PaymentNoticeMatchResult.success로 판단해야 함)
export function matchPaymentNotice(
  organizationId: string,
  items: PaymentNoticeMatchRequest[]
): Promise<any> {
  if (!organizationId) throw new Error("Organization ID is required.");
  if (!items.length)
    throw new Error("Payment notice match items are required.");
  return http.put(`${paymentNoticeUrl}/${organizationId}/match`, items);
}

// 납부서 삭제 (DB 도큐먼트 + S3 원본 파일 하드 삭제, 복구 불가)
// organizationId 경로 세그먼트 없음 - 권한 체크는 서버가 대상 납부서의 businessId 또는
// organizationId 중 하나로 알아서 판단한다 (OR 조건)
export function deletePaymentNotice(paymentNoticeId: string): Promise<any> {
  if (!paymentNoticeId) throw new Error("Payment notice ID is required.");
  return http.delete(`${paymentNoticeUrl}/${paymentNoticeId}`);
}

// 납부서 중복 체크 (사업자+귀속연월+세목이 모두 같은 다른 납부서가 있는 건만, 그 조합에
// 속한 전체 납부서를 반환 - 요청 id 외에 이미 DB에 있던 다른 납부서도 포함될 수 있음)
// organizationId 경로 세그먼트 없음 - 권한 없는 건은 에러 대신 응답에서 조용히 제외된다
export function checkDuplicatePaymentNotices(
  paymentNoticeIds: string[]
): Promise<any> {
  if (!paymentNoticeIds.length)
    throw new Error("Payment notice IDs are required.");
  return http.post(`${paymentNoticeUrl}/duplicate-check`, paymentNoticeIds);
}

// 매칭 후보 사업자 목록 (simple=true는 { businessDtos: SimpleBusiness[] } 형태로 응답)
export function getSimpleBusinessList(): Promise<any> {
  return http.get("/business", { params: { simple: true } });
}

// 사업자별 납부서 매칭 현황 (건수, 귀속연월 기준), organizationId 생략 시 서버가 소속 조직 중 자동 선택
export function getBusinessPaymentNoticeCounts(
  period: string,
  organizationId?: string
): Promise<any> {
  if (!period) throw new Error("Period is required.");
  return http.get(paymentNoticeUrl, {
    params: { period, ...(organizationId ? { organizationId } : {}) }
  });
}

// 특정 사업자에 매칭 확정된 납부서 목록 (귀속연월 기준)
export function getBusinessPaymentNotices(
  businessId: string,
  period: string
): Promise<any> {
  if (!businessId) throw new Error("Business ID is required.");
  if (!period) throw new Error("Period is required.");
  return http.get(`${paymentNoticeUrl}/${businessId}`, { params: { period } });
}

// 납부서 카카오 알림톡 발송 (귀속연월 기준, 호출당 사업자 1건)
export function sendPaymentNoticeKakao(
  businessId: string,
  period: string
): Promise<any> {
  if (!businessId) throw new Error("Business ID is required.");
  if (!period) throw new Error("Period is required.");
  return http.post(`${paymentNoticeUrl}/${businessId}/kakao`, null, {
    params: { period }
  });
}

// 2.8 납부서 카카오 알림톡 발송 이력 조회 (귀속연월 기준, 발송 순서대로).
// 응답 data: KakaoNotificationLog[] — 발송한 적 없으면 빈 배열.
export function getPaymentNoticeKakaoHistory(
  businessId: string,
  period: string
): Promise<any> {
  if (!businessId) throw new Error("Business ID is required.");
  if (!period) throw new Error("Period is required.");
  return http.get(`${paymentNoticeUrl}/${businessId}/kakao`, {
    params: { period }
  });
}

// 2.10 부가세/종소세/법인세 납부서 업로드 현황 조회 (period = 납부연월, 변환 없음)
// 응답 스키마는 2.5(getBusinessPaymentNoticeCounts)와 같고, 건수·kakaoSentAt이 type 기준이다.
export function getOtherTaxPaymentNoticeCounts(
  period: string,
  type: OtherTaxType,
  organizationId?: string
): Promise<any> {
  if (!period) throw new Error("Period is required.");
  return http.get(`${paymentNoticeUrl}/tax`, {
    params: { period, type, ...(organizationId ? { organizationId } : {}) }
  });
}

// 2.11 사업자별 부가세/종소세/법인세 납부서 상세 내역 조회
export function getOtherTaxBusinessPaymentNotices(
  businessId: string,
  period: string,
  type: OtherTaxType
): Promise<any> {
  if (!businessId) throw new Error("Business ID is required.");
  if (!period) throw new Error("Period is required.");
  return http.get(`${paymentNoticeUrl}/tax/${businessId}`, {
    params: { period, type }
  });
}

// 2.13 부가세/종소세/법인세 납부서 알림톡 발송 (호출당 사업자 1건)
export function sendOtherTaxPaymentNoticeKakao(
  businessId: string,
  period: string,
  type: OtherTaxType
): Promise<any> {
  if (!businessId) throw new Error("Business ID is required.");
  if (!period) throw new Error("Period is required.");
  return http.post(`${paymentNoticeUrl}/tax/${businessId}/kakao`, null, {
    params: { period, type }
  });
}

// 2.12 부가세/종소세/법인세 알림톡 발송 이력 조회 (발송한 적 없으면 빈 배열)
export function getOtherTaxPaymentNoticeKakaoHistory(
  businessId: string,
  period: string,
  type: OtherTaxType
): Promise<any> {
  if (!businessId) throw new Error("Business ID is required.");
  if (!period) throw new Error("Period is required.");
  return http.get(`${paymentNoticeUrl}/tax/${businessId}/kakao`, {
    params: { period, type }
  });
}

//// 급여관리 PRO (payroll-management)
// API 정의서: BackEnd/docs/api/payroll-management-api.md

const payrollManagementUrl = "/smartcta/payroll-management";

// yearMonth 파라미터는 YYYYMM 문자열. 화면 URL은 YYYY-MM(period)이므로 호출 직전에 변환한다.
export const toYearMonthParam = (period: string) => period.replace("-", "");

// 2.1 거래처 월간 현황 표 조회
export function getPayrollGrid(
  yearMonth: string,
  organizationId?: string
): Promise<any> {
  if (!yearMonth) throw new Error("yearMonth is required.");
  return http.get(`${payrollManagementUrl}/grid`, {
    params: {
      yearMonth: toYearMonthParam(yearMonth),
      ...(organizationId ? { organizationId } : {})
    }
  });
}

// 2.3 담당자 수동 체크 칸 완료/해제
export function patchPayrollManualCheck(body: {
  businessId: string;
  yearMonth: string;
  cell: string;
  done: boolean;
}): Promise<any> {
  return http.patch(`${payrollManagementUrl}/manual-check`, {
    ...body,
    yearMonth: toYearMonthParam(body.yearMonth)
  });
}

// 2.4 퇴사자 퇴직정산 완료/해제 (PRO 퇴직 칸 클릭)
// 지급월 무관 — 정산요청 payMonth를 따라간다. 응답은 갱신된 SeveranceRequest.
export function patchRetirementSettlement(body: {
  businessId: string;
  payeeId: string;
  settled: boolean;
}): Promise<any> {
  return http.patch(`${payrollManagementUrl}/retirement-settlement`, body);
}

// 2.10 퇴사자 정산요청 패널 조회 (조회 전용, 탭 2개)
// 응답 data: SeveranceRequestPanel { incomeTaxSettlements, severancePaySettlements }
export function getSeveranceRequests(
  yearMonth: string, // YYYY-MM (period)
  organizationId?: string
): Promise<any> {
  if (!yearMonth) throw new Error("yearMonth is required.");
  return http.get(`${payrollManagementUrl}/severance-requests`, {
    params: {
      yearMonth: toYearMonthParam(yearMonth),
      ...(organizationId ? { organizationId } : {})
    }
  });
}

// 2.15 공단 취득·상실신고 패널 조회
// yearMonth(지급월) 그대로 기준 — 취득일·상실일이 조회월 또는 전월인 직원. 귀속월 변환 없음.
// 응답 data: InsuranceReportPanel { acquisitions, losses }
export function getInsuranceReports(
  yearMonth: string, // YYYY-MM (period)
  organizationId?: string
): Promise<any> {
  if (!yearMonth) throw new Error("yearMonth is required.");
  return http.get(`${payrollManagementUrl}/insurance-reports`, {
    params: {
      yearMonth: toYearMonthParam(yearMonth),
      ...(organizationId ? { organizationId } : {})
    }
  });
}

// 2.16 공단 취득·상실신고 처리 완료/해제 (직원 1명)
// 응답 data: 완료 → { filedAt, filedByUser } / 해제 → null. 회사 직접 신고(locked) 건은 E002.
export function patchInsuranceFiling(body: {
  businessId: string;
  payeeId: string;
  kind: "ACQUISITION" | "LOSS";
  filed: boolean;
}): Promise<any> {
  return http.patch(`${payrollManagementUrl}/insurance-filing`, body);
}

// 2.17 급여 확인요청 · 마감 해제 요청 목록 (기관 전체)
// 대기(PENDING)는 달과 상관없이 전부, 회신완료는 이 지급월에 담기는 귀속월 것만 온다.
export function getPayrollConfirmRequests(
  yearMonth: string, // YYYY-MM (period)
  organizationId?: string
): Promise<any> {
  if (!yearMonth) throw new Error("yearMonth is required.");
  return http.get(`${payrollManagementUrl}/confirm-requests`, {
    params: {
      yearMonth: toYearMonthParam(yearMonth),
      ...(organizationId ? { organizationId } : {})
    }
  });
}

// payroll-input-api.md 2.9 확인요청 회신 — reply 를 비우면 회신 내용 없이 처리완료.
// 이미 회신한 건에 다시 보내면 회신 내용을 고친다 (대기로 되돌리는 기능은 없음).
export function replyPayrollConfirmRequest(
  requestId: string,
  body: { businessId: string; reply?: string | null }
): Promise<any> {
  return http.put(`/payroll/simple/confirm-requests/${requestId}/reply`, body);
}

// payroll-input-api.md 2.5 급여 마감 해제 (그 달 네 유형 전부).
// 그 달 대기 중인 마감 해제 요청은 서버가 자동으로 회신완료 처리한다.
export function deletePayrollSimpleClosing(
  businessId: string,
  yearMonth: string // 귀속연월 YYYYMM
): Promise<any> {
  return http.delete(`/payroll/simple/closing`, {
    params: { businessId, yearMonth }
  });
}

// 2.9 급여 세무사랑(뉴젠) 전송 (PRO 화면 전용 — 2.8과 별개 엔드포인트)
// yearMonth는 지급월(period)이 아니라 귀속연월 yyyy-MM 임에 유의 (BR-03, grid.logic.accrualPeriod)
export function uploadPayrollToSemusarang(body: {
  businessId: string;
  yearMonth: string; // yyyy-MM (귀속연월)
  payeeType: "EMPLOYEE" | "DAILY_WORKER" | "BUSINESS_INCOME" | "OTHER_INCOME";
}): Promise<any> {
  return http.post(`${payrollManagementUrl}/upload-payroll`, body);
}

// 2.5 거래처 예외 업무 체크리스트 — 항목 추가 (거래처 단위)
// text·repeat·repeatMonth 는 생성 후 변경 불가. 응답(PayrollMemoView[])은 yearMonth 기준으로
// 완료/활성을 판정한 전체 목록. 반복 메모는 주기별 completions로 관리돼 배치 초기화가 없다.
export function addPayrollMemo(body: {
  businessId: string;
  yearMonth: string; // YYYY-MM (period)
  text: string;
  repeat?: "NONE" | "MONTHLY" | "YEARLY";
  repeatMonth?: number | null;
}): Promise<any> {
  return http.post(`${payrollManagementUrl}/memo`, {
    ...body,
    yearMonth: toYearMonthParam(body.yearMonth)
  });
}

// 2.6 체크리스트 항목 완료/해제 토글
// yearMonth 가 속한 주기(NONE=ONCE / MONTHLY=yyyy-MM / YEARLY=주기연도)에 완료 기록을 남기거나 지운다.
// doneDate 생략 시 서버가 오늘 날짜로 기록.
export function setPayrollMemoDone(body: {
  businessId: string;
  memoId: string;
  yearMonth: string; // YYYY-MM (period)
  done: boolean;
  doneDate?: string; // yyyy-MM-dd
}): Promise<any> {
  return http.patch(`${payrollManagementUrl}/memo/done`, {
    ...body,
    yearMonth: toYearMonthParam(body.yearMonth)
  });
}

// 2.7 체크리스트 항목 삭제
export function deletePayrollMemo(
  businessId: string,
  memoId: string,
  yearMonth: string // YYYY-MM (period)
): Promise<any> {
  return http.delete(`${payrollManagementUrl}/memo`, {
    params: { businessId, memoId, yearMonth: toYearMonthParam(yearMonth) }
  });
}

// NOTE: 세특 [급여입력] 팝업의 정산요청 생성/수정 API(§2.11~2.13 PUT/GET/DELETE /api/v2/severance-request)와
// 관리 목록(§2.14 GET /severance-requests/all)은
// 세특 본체 화면 몫이라 여기서는 다루지 않는다. PRO는 §2.10 조회 + §2.4 완료토글만 사용.
